import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { useA11ySelector } from '@sg/a11y-helpers';
import { useResponsive } from '@sg/garnish';

import type { useCreditCardOptions } from '@order/integrations/stripe';

import { PaymentMethodSelectorModal } from '../../../../../../components';
import { getCreditCardIcon } from '../../../../../../constants';
import { type GiftCardCheckoutFormReturn } from '../../../../form';
import { GiftCardCheckoutFormGroup } from '../../components';
import { giftCardCheckoutPaymentMethodMessages as messages } from './GiftCardCheckoutPaymentMethod.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutPaymentMethod = (props: GiftCardCheckoutPaymentMethodProps) => {
  const { form, paymentMethods } = props;

  const paymentMethodId = form.watch('paymentMethodId');

  const paymentMethodIdError = form.formState.errors.paymentMethodId?.message;

  const a11ySelector = useA11ySelector('gift-cards.customization.payment-option'); // prettier-ignore
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const selectedPaymentMethod = useMemo(() => {
    return paymentMethods.find((paymentMethod) => paymentMethod?.value === paymentMethodId);
  }, [paymentMethods, paymentMethodId]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onPaymentMethodSelected = useCallback(
    (selectedPaymentMethodId: string) => {
      form.clearErrors('paymentMethodId');
      form.setValue('paymentMethodId', selectedPaymentMethodId);
    },
    [form],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = match([styles.containerXS, styles.containerSM]);

  // ─────────────────────────────────────────────────────────────────────

  const selectedPaymentMethodLabel = selectedPaymentMethod?.label ?? '';
  const creditCardIcon = getCreditCardIcon(selectedPaymentMethod?.cardType);

  return (
    <GiftCardCheckoutFormGroup.Container style={containerStyles} {...a11ySelector.props}>
      <View style={styles.titleContainer}>
        <GiftCardCheckoutFormGroup.Title>
          <FormattedMessage {...messages.sectionTitle} />
        </GiftCardCheckoutFormGroup.Title>
      </View>

      <PaymentMethodSelectorModal
        selectedPaymentMethodId={paymentMethodId}
        selectedPaymentMethodIcon={creditCardIcon}
        selectedPaymentMethodLabel={selectedPaymentMethodLabel}
        onPaymentMethodSelected={onPaymentMethodSelected}
        paymentMethodOptions={paymentMethods}
        paymentMethodSelectionNotice={paymentMethodIdError}
        paymentMethodSelectionNoticePalette="caution"
        hasBoldLabel={false}
      />
    </GiftCardCheckoutFormGroup.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingBottom: 0,
  },
  containerSM: {
    borderTopWidth: 0,
    borderBottomWidth: 0,
    borderBottomStartRadius: theme.radius.large,
    borderBottomEndRadius: theme.radius.large,
    paddingBottom: theme.spacing['4'],
  },
  titleContainer: {
    marginBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutPaymentMethodProps = {
  sectionTitle?: string;
  form: GiftCardCheckoutFormReturn;
  paymentMethods: ReturnType<typeof useCreditCardOptions>['creditCardOptions'];
};
